import React, { useState, useEffect } from 'react';
import { budgetsAPI } from '../api/client';
import { TrendingUp, TrendingDown, PoundSterling, AlertTriangle, PieChart, RefreshCw, ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function BudgetDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const { user } = useAuth();
  
  // Check if user is Department Manager
  const isDepartmentManager = user?.roles?.some(r => r.RoleName === 'Departments Manager');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      if (isDepartmentManager) {
        // Department Managers: Get only their department budgets
        const response = await budgetsAPI.getMyDepartmentBudgets();
        setBudgets(response.data.budgets || []);
        setDashboardData(null); // No company-wide dashboard
      } else {
        // Finance/Super Admin: Get company-wide dashboard
        const response = await budgetsAPI.getDashboard();
        setDashboardData(response.data);
      }
    } catch (err) {
      console.error('Error fetching budget dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Department Manager View
  if (isDepartmentManager) {
    if (!budgets || budgets.length === 0) {
      return (
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Department Budget</h1>
            <p className="text-gray-600 mt-1">Manage your department's budget</p>
          </div>
          <div className="card">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900">No Budgets Assigned</h3>
                <p className="text-gray-600 mt-2">
                  You don't have any departments assigned to you, or your departments don't have budgets set up yet.
                </p>
                <p className="text-sm text-gray-500 mt-4">
                  Contact your Finance Admin to set up budgets for your departments.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show department budgets
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Department Budget</h1>
            <p className="text-gray-600 mt-1">Manage your department's budget allocation</p>
          </div>
          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {budgets.map((budget) => {
          const total = budget.TotalBudget || 0;
          const allocated = budget.AllocatedBudget || 0;
          const spent = budget.SpentAmount || 0;
          const committed = budget.CommittedAmount || 0;
          const remaining = budget.RemainingAmount || 0;
          const utilization = total > 0 ? ((spent / total) * 100).toFixed(1) : 0;

          return (
            <div key={budget.BudgetID} className="space-y-6">
              {/* Budget Header */}
              <div className="card bg-blue-50 border-l-4 border-blue-600">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{budget.BudgetName}</h2>
                    <p className="text-gray-600 mt-1">{budget.DepartmentName}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {budget.YearLabel} • Status: <span className="font-semibold">{budget.Status}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Budget</p>
                    <p className="text-3xl font-bold text-blue-600">
                      £{total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card">
                  <p className="text-sm text-gray-600">Allocated</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    £{allocated.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Spent</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    £{spent.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Committed</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">
                    £{committed.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Remaining</p>
                  <p className={`text-2xl font-bold mt-1 ${remaining < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    £{remaining.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Utilization Bar */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Budget Utilization</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className={`h-4 rounded-full ${
                          utilization >= 100 ? 'bg-red-600' :
                          utilization >= 80 ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(utilization, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${
                    utilization >= 100 ? 'text-red-600' :
                    utilization >= 80 ? 'text-orange-600' :
                    'text-green-600'
                  }`}>
                    {utilization}%
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="card">
                <div className="flex gap-4">
                  <button
                    onClick={() => window.location.href = `/budgets/${budget.BudgetID}/manage`}
                    className="btn-primary"
                  >
                    Manage Budget Lines
                  </button>
                  {budget.Status === 'Draft' && (
                    <button
                      className="btn-secondary"
                      onClick={async () => {
                        if (!window.confirm('Send this budget for approval?')) return;
                        try {
                          await budgetsAPI.update(budget.BudgetID, { Status: 'Pending Approval' });
                          fetchDashboardData();
                        } catch (err) {
                          console.error('Failed to send budget for approval:', err);
                          alert(err.response?.data?.error || 'Failed to send for approval');
                        }
                      }}
                    >
                      Send for Approval
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Finance/Admin View (Original Dashboard)
  const summary = dashboardData?.summary || {};
  const departments = dashboardData?.departments || [];

  const utilizationRate = summary.TotalBudget > 0 
    ? ((summary.TotalSpent / summary.TotalBudget) * 100).toFixed(1)
    : 0;

  const getUtilizationColor = (rate) => {
    if (rate >= 100) return 'text-red-600';
    if (rate >= 80) return 'text-orange-600';
    return 'text-green-600';
  };

  const getDeptUtilizationColor = (util) => {
    if (util >= 100) return 'bg-red-100 text-red-800';
    if (util >= 80) return 'bg-orange-100 text-orange-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Budget Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview of budget allocation and utilization</p>
        </div>
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Budget */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Budget</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                £{(summary.TotalBudget || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <PoundSterling className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Total Spent */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                £{(summary.TotalSpent || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Committed */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Committed</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                £{(summary.TotalCommitted || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <PieChart className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Remaining */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Remaining</p>
              <p className={`text-2xl font-bold mt-1 ${summary.TotalRemaining < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                £{(summary.TotalRemaining || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className={`p-3 rounded-full ${summary.TotalRemaining < 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              {summary.TotalRemaining < 0 ? (
                <AlertTriangle className="w-6 h-6 text-red-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-green-600" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Utilization Rate */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Overall Utilization</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className={`h-4 rounded-full ${
                  utilizationRate >= 100 ? 'bg-red-600' :
                  utilizationRate >= 80 ? 'bg-orange-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${Math.min(utilizationRate, 100)}%` }}
              />
            </div>
          </div>
          <div className={`text-2xl font-bold ${getUtilizationColor(utilizationRate)}`}>
            {utilizationRate}%
          </div>
        </div>
      </div>

      {/* Department Budget Breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Department Budget Utilization</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Budget</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Spent</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Committed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Utilization</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {departments.map((dept, index) => {
                const util = dept.Budget > 0 ? ((dept.Spent / dept.Budget) * 100).toFixed(1) : 0;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                      {dept.Department || 'Unassigned'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900">
                      £{(dept.Budget || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900">
                      £{(dept.Spent || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                      £{(dept.Committed || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-right font-medium ${dept.Remaining < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      £{(dept.Remaining || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDeptUtilizationColor(util)}`}>
                        {util}%
                      </span>
                    </td>
                  </tr>
                );
              })}
              {departments.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No budget data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alerts */}
      {departments.some(d => d.Budget > 0 && (d.Spent / d.Budget) >= 0.8) && (
        <div className="card border-l-4 border-orange-500 bg-orange-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-orange-900">Budget Alerts</h4>
              <ul className="mt-2 space-y-1 text-sm text-orange-800">
                {departments
                  .filter(d => d.Budget > 0 && (d.Spent / d.Budget) >= 0.8)
                  .map((dept, i) => {
                    const util = ((dept.Spent / dept.Budget) * 100).toFixed(0);
                    return (
                      <li key={i}>
                        • <strong>{dept.Department}</strong>: {util}% utilized
                        {util >= 100 && ' - OVER BUDGET!'}
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
